/**
 * The alias each exposed column travels under (CT-5529).
 *
 * The alias is what `visualizationConfig.bindings` points at through
 * `field_key`, and what the executor names the column in the result. It used
 * to be positional — `col_1`, `col_2` — which is stable but says nothing: a
 * reader of the saved definition, or of an `execute` response, cannot tell
 * which field `col_7` is without resolving its uuid against the catalog.
 *
 * So it is the **source field's code** now, which the ticket asks for and
 * which makes both the definition and the result readable.
 *
 * ## Three ways a field code is not a usable alias
 *
 * The presentation schema constrains an alias to
 * `^[a-z][a-z0-9_]{0,59}$`, and two aliases are already spoken for, so a raw
 * code cannot simply be copied:
 *
 *   - **Shape.** Anything outside `a-z0-9_` becomes `_`, uppercase is folded,
 *     a code that does not start with a letter is prefixed, and the whole
 *     thing is cut to 60 characters.
 *   - **Collision with the reserved two.** A field actually called
 *     `record_count` or `record_uuid` would silently take the aggregate's or
 *     the record key's place in the query, and the binding would read the
 *     wrong column.
 *   - **Collision with another column.** Two members of a composite can both
 *     expose `employee_code`; after folding, `Start Date` and `start_date`
 *     are the same string too.
 *
 * Every clash is resolved by suffixing `_2`, `_3`… in the order the columns
 * were chosen, so the result is deterministic: the same selection always
 * produces the same aliases, which is what keeps a re-save from rewriting a
 * definition that did not change.
 *
 * Falls back to the positional alias when a column has no usable code at all,
 * so a dataset column — which has no field code — keeps working.
 *
 * The shaping and the de-duplication themselves live in `widgetFieldKey`: the
 * rev4 contract names the same grammar `field_key` and derives one for every
 * binding, so the rule has two callers now and one owner (CT-5641).
 */

import { shapeFieldKey, uniqueFieldKey } from './widgetFieldKey.js';

/**
 * @param {string[]} fieldUuids The exposed columns, in order.
 * @param {object[]} columns Loaded column metadata, read for `internalName`.
 * @param {(index: number) => string} positional The old scheme, used as the
 *   fallback for a column with no usable code.
 * @param {string[]} [reserved] Aliases the payload builds itself — the
 *   aggregate's and the record key's. Passed in rather than imported: this
 *   module is imported *by* the one that owns them, and reaching back for
 *   them closes a cycle that leaves both half-initialised. It does not throw
 *   in a unit test, where the graph resolves the other way round; it blanks
 *   the app in the browser.
 * @returns {Map<string, string>} `fieldUuid → alias`.
 */
export function buildColumnAliases(fieldUuids = [], columns = [], positional, reserved = []) {
  const codeByUuid = new Map(columns.map((column) => [column.fieldUuid, column.internalName]));
  const taken = new Set(reserved);
  const aliases = new Map();

  fieldUuids.forEach((fieldUuid, index) => {
    const base = shapeFieldKey(codeByUuid.get(fieldUuid)) || positional(index);
    // `_2` onwards: the first column to claim a name keeps it.
    const alias = uniqueFieldKey(base, taken);
    taken.add(alias);
    aliases.set(fieldUuid, alias);
  });

  return aliases;
}

export default buildColumnAliases;
